import type { ForecastDaySummary } from '../components/weather/WeatherDashboard';

export interface ForecastSummaryBullets {
  today: string;
  week: string;
  /** Shown when wind, heat, fog, or other safety-relevant conditions apply. */
  conditions?: string;
}

function todayConditionPhrase(code?: number | null): string | null {
  if (code == null) return null;
  if (code === 0) return 'clear skies';
  if (code <= 3) return 'partly cloudy skies';
  if (code <= 48) return 'fog';
  if (code <= 55) return 'drizzle';
  if (code <= 67) return 'rain likely';
  if (code <= 77) return 'snow possible';
  if (code <= 82) return 'showers';
  if (code <= 86) return 'snow showers';
  return 'a chance of thunderstorms';
}

function buildTodayBullet(today: ForecastDaySummary): string {
  const temp =
    today.temperature_c != null ? `Near ${Math.round(today.temperature_c)}°C` : null;
  const condition = todayConditionPhrase(today.weather_code);

  if (temp && condition) {
    return `${temp} with ${condition}.`;
  }
  if (temp) return `${temp}.`;
  if (condition) {
    return condition.startsWith('a ')
      ? `Expect ${condition}.`
      : `Expect ${condition} today.`;
  }
  return 'Mixed conditions through the day.';
}

function buildWeekBullet(days: ForecastDaySummary[], weekRain: number): string {
  const temps = days
    .map((d) => d.temperature_c)
    .filter((t): t is number => t != null);
  const minTemp = temps.length ? Math.round(Math.min(...temps)) : null;
  const maxTemp = temps.length ? Math.round(Math.max(...temps)) : null;

  const rainDays = days.filter((d) => (d.precipitation_mm ?? 0) >= 5).length;

  let headline: string;
  if (weekRain >= 25 || rainDays >= 4) {
    headline = 'Wet and rainy on multiple days';
  } else if (weekRain >= 8 || rainDays >= 2) {
    headline = 'Some rain expected across the week';
  } else if (weekRain <= 2) {
    headline = 'Mostly dry with little rain expected';
  } else {
    headline = 'A mix of dry and showery days';
  }

  const extras: string[] = [];
  if (maxTemp != null && minTemp != null && maxTemp - minTemp >= 5) {
    extras.push(`temperatures from ${minTemp}° to ${maxTemp}°`);
  } else if (maxTemp != null && maxTemp >= 32) {
    extras.push('hot spells on warmer days');
  } else if (maxTemp != null && maxTemp <= 24) {
    extras.push('cooler days ahead');
  }

  if (extras.length === 0) return `${headline}.`;
  return `${headline}, with ${extras.join(' and ')}.`;
}

function capitalizeSentence(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function buildConditionsBullet(
  days: ForecastDaySummary[],
  maxWind: number,
): string | undefined {
  const today = days[0];
  const sentences: string[] = [];

  if (maxWind >= 14) {
    sentences.push('Strong winds possible');
  } else if (maxWind >= 10) {
    sentences.push('Breezy at times');
  }

  if (today.temperature_c != null && today.temperature_c >= 35) {
    sentences.push('High heat today');
  } else if (days.some((d) => (d.temperature_c ?? 0) >= 35)) {
    sentences.push('High heat on some days');
  }

  if (today.weather_code != null && today.weather_code <= 48 && today.weather_code > 0) {
    sentences.push('Reduced visibility from fog');
  }

  if (sentences.length === 0) return undefined;

  if (maxWind >= 10) {
    sentences.push('Take care on exposed routes');
  } else if (today.temperature_c != null && today.temperature_c >= 35) {
    sentences.push('Stay hydrated and limit midday exposure');
  }

  return sentences.map((sentence) => {
    const normalized = capitalizeSentence(sentence.trim());
    return normalized.endsWith('.') ? normalized : `${normalized}.`;
  }).join(' ');
}

/**
 * Bulleted warning-style summary from the user's 7-day forecast rows.
 *
 * Inputs come from GET /weather/forecast/at (nearest ~2 km weather cell at GPS):
 * - temperature_c, precipitation_mm, wind_speed_ms per day
 * - weather_code (WMO-style code) for today's sky condition
 */
export function buildForecastSummary(days: ForecastDaySummary[]): ForecastSummaryBullets | null {
  if (days.length === 0) return null;

  const today = days[0];
  const weekRain = days.reduce((sum, d) => sum + (d.precipitation_mm ?? 0), 0);
  const maxWind = Math.max(...days.map((d) => d.wind_speed_ms ?? 0));

  const conditions = buildConditionsBullet(days, maxWind);

  return {
    today: buildTodayBullet(today),
    week: buildWeekBullet(days, weekRain),
    ...(conditions ? { conditions } : {}),
  };
}

/** Plain string for email digests and other non-UI consumers. */
export function formatForecastSummaryPlain(bullets: ForecastSummaryBullets | null): string {
  if (!bullets) {
    return 'Fetching the forecast for your area. This usually takes a moment.';
  }
  const lines = [`Today: ${bullets.today}`, `The Week: ${bullets.week}`];
  if (bullets.conditions) {
    lines.push(`Conditions: ${bullets.conditions}`);
  }
  return lines.join(' ');
}
